import apiClient from "../utils/apiUtils";

export const payrollService = {
  // ==================== DASHBOARD APIs ====================
  getDashboardStats: async () => {
    const response = await apiClient.get("/payroll/dashboard/stats");
    return response.data;
  },

  getDashboardCharts: async () => {
    const response = await apiClient.get("/payroll/dashboard/charts");
    return response.data;
  },

  getRecentActivities: async () => {
    const response = await apiClient.get("/payroll/dashboard/recent-activities");
    return response.data;
  },

  getExecutiveSummary: async () => {
    const response = await apiClient.get("/payroll/dashboard/executive-summary");
    return response.data;
  },

  // ==================== APPROVAL WORKFLOW APIs ====================
  submitForApproval: async (id, payload) => {
    const response = await apiClient.post(
      `/payroll/${id}/submit-for-approval`,
      payload
    );
    return response.data;
  },

  approvePayroll: async (id, payload) => {
    const response = await apiClient.post(`/payroll/${id}/approve`, payload);
    return response.data;
  },

  rejectPayroll: async (id, payload) => {
    const response = await apiClient.post(`/payroll/${id}/reject`, payload);
    return response.data;
  },

  cancelApproval: async (id, payload) => {
    const response = await apiClient.post(`/payroll/${id}/cancel-approval`, payload);
    return response.data;
  },

  getPendingApprovals: async (params) => {
    const response = await apiClient.get("/payroll/pending-approvals", { params });
    return response.data;
  },

  getApprovalHistory: async (id) => {
    const response = await apiClient.get(`/payroll/${id}/approval-history`);
    return response.data;
  },

  // ==================== REPORTING APIs ====================
  generateReport: async (payload) => {
    const response = await apiClient.post("/payroll/reports/generate", payload);
    return response.data;
  },

  getReportTemplates: async () => {
    const response = await apiClient.get("/payroll/reports/templates");
    return response.data;
  },

  scheduleReport: async (payload) => {
    const response = await apiClient.post("/payroll/reports/schedule", payload);
    return response.data;
  },

  getReport: async (id) => {
    const response = await apiClient.get(`/payroll/reports/${id}`);
    return response.data;
  },

  deleteReport: async (id) => {
    const response = await apiClient.delete(`/payroll/reports/${id}`);
    return response.data;
  },

  // ==================== EXPORT APIs ====================
  generateBankFile: async (payload) => {
    const response = await apiClient.post("/payroll/export/bank-file", payload, {
      responseType: "blob",
    });
    return response.data;
  },

  generateStatutoryReport: async (payload) => {
    const response = await apiClient.post(
      "/payroll/export/statutory-report",
      payload,
      {
        responseType: "blob",
      }
    );
    return response.data;
  },

  exportAccountingData: async (payload) => {
    const response = await apiClient.post(
      "/payroll/export/accounting-data",
      payload,
      {
        responseType: "blob",
      }
    );
    return response.data;
  },

  exportPayrollData: async (payload) => {
    const response = await apiClient.post("/payroll/export/payroll-data", payload, {
      responseType: "blob",
    });
    return response.data;
  },

  // ==================== EMPLOYEE SELF-SERVICE APIs ====================
  getMyPayrolls: async (params) => {
    const response = await apiClient.get("/employee/payroll/my-payrolls", { params });
    return response.data;
  },

  getMyPayrollSummary: async () => {
    const response = await apiClient.get("/employee/payroll/summary");
    return response.data;
  },

  getMyPayrollDetails: async (id) => {
    const response = await apiClient.get(`/employee/payroll/${id}`);
    return response.data;
  },

  requestPayrollCorrection: async (id, payload) => {
    const response = await apiClient.post(
      `/employee/payroll/${id}/request-correction`,
      payload
    );
    return response.data;
  },

  getPayslipDownloadLink: async (id) => {
    const response = await apiClient.get(`/employee/payroll/${id}/payslip-download`);
    return response.data;
  },

  // ==================== CONFIGURATION APIs ====================
  getAllConfigurations: async (params) => {
    const response = await apiClient.get("/payroll/config", { params });
    return response.data;
  },

  getSalaryConfigurations: async () => {
    const response = await apiClient.get("/payroll/salary-configurations");
    return response.data;
  },

  createConfiguration: async (payload) => {
    const response = await apiClient.post("/payroll/config", payload);
    return response.data;
  },

  getConfiguration: async (id) => {
    const response = await apiClient.get(`/payroll/config/${id}`);
    return response.data;
  },

  updateConfiguration: async (id, payload) => {
    const response = await apiClient.put(`/payroll/config/${id}`, payload);
    return response.data;
  },

  deleteConfiguration: async (id) => {
    const response = await apiClient.delete(`/payroll/config/${id}`);
    return response.data;
  },

  getConfigSettings: async () => {
    const response = await apiClient.get("/payroll/config/settings");
    return response.data;
  },

  updateConfigSettings: async (payload) => {
    const response = await apiClient.post("/payroll/config/settings", payload);
    return response.data;
  },

  getConfigSettingValue: async (key) => {
    const response = await apiClient.get(`/payroll/config/settings/${key}`);
    return response.data;
  },

  getActiveConfiguration: async () => {
    const response = await apiClient.get("/payroll/config/active");
    return response.data;
  },

  // ==================== SETTINGS APIs ====================
  getAllSettings: async (params) => {
    const response = await apiClient.get("/payroll/settings", { params });
    return response.data;
  },

  getSetting: async (id) => {
    const response = await apiClient.get(`/payroll/settings/${id}`);
    return response.data;
  },

  createSetting: async (payload) => {
    const response = await apiClient.post("/payroll/settings", payload);
    return response.data;
  },

  updateSetting: async (id, payload) => {
    const response = await apiClient.put(`/payroll/settings/${id}`, payload);
    return response.data;
  },

  bulkUpdateSettings: async (payload) => {
    const response = await apiClient.post("/payroll/settings-bulk-update", payload);
    return response.data;
  },

  deleteSetting: async (id) => {
    const response = await apiClient.delete(`/payroll/settings/${id}`);
    return response.data;
  },

  getSettingsByCategory: async (category) => {
    const response = await apiClient.get(`/payroll/settings/category/${category}`);
    return response.data;
  },

  // ==================== SALARY STRUCTURE APIs ====================
  getAllSalaryStructures: async (params) => {
    const response = await apiClient.get("/payroll/salary-structures", { params });
    return response.data;
  },

  createSalaryStructure: async (payload) => {
    const response = await apiClient.post("/payroll/salary-structures", payload);
    return response.data;
  },

  getSalaryStructure: async (id) => {
    const response = await apiClient.get(`/payroll/salary-structures/${id}`);
    return response.data;
  },

  updateSalaryStructure: async (id, payload) => {
    const response = await apiClient.put(`/payroll/salary-structures/${id}`, payload);
    return response.data;
  },

  deleteSalaryStructure: async (id) => {
    const response = await apiClient.delete(`/payroll/salary-structures/${id}`);
    return response.data;
  },

  getEmployeeSalaryStructure: async (employeeId) => {
    const response = await apiClient.get(
      `/payroll/employees/${employeeId}/salary-structure`
    );
    return response.data;
  },

  updateEmployeeSalaryStructure: async (employeeId, payload) => {
    const response = await apiClient.post(
      `/payroll/employees/${employeeId}/salary-structure`,
      payload
    );
    return response.data;
  },

  getSalaryHistory: async (employeeId, params) => {
    const response = await apiClient.get(
      `/payroll/salary-structures/${employeeId}/history`,
      { params }
    );
    return response.data;
  },

  bulkUpdateSalaries: async (payload) => {
    const response = await apiClient.post(
      "/payroll/salary-structures/bulk-update",
      payload
    );
    return response.data;
  },

  generateSalaryTemplate: async () => {
    const response = await apiClient.post(
      "/payroll/salary-structures/generate-template"
    );
    return response.data;
  },

  downloadSalaryTemplate: async () => {
    const response = await apiClient.get(
      "/payroll/salary-structures/template/download",
      {
        responseType: "blob",
      }
    );
    return response.data;
  },

  importSalaryStructures: async (formData) => {
    const response = await apiClient.post(
      "/payroll/salary-structures/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  exportSalaryStructures: async () => {
    const response = await apiClient.get("/payroll/salary-structures/export/verify", {
      responseType: "blob",
    });
    return response.data;
  },

  // ==================== COMPONENT TYPES APIs ====================
  getAllComponentTypes: async (params) => {
    const response = await apiClient.get("/payroll/component-types", { params });
    return response.data;
  },

  createComponentType: async (payload) => {
    const response = await apiClient.post("/component-types", payload);
    return response.data;
  },

  getComponentType: async (id) => {
    const response = await apiClient.get(`/component-types/${id}`);
    return response.data;
  },

  updateComponentType: async (id, payload) => {
    const response = await apiClient.put(`/component-types/${id}`, payload);
    return response.data;
  },

  deleteComponentType: async (id) => {
    const response = await apiClient.delete(`/component-types/${id}`);
    return response.data;
  },

  getComponentTypeOptions: async (params) => {
    const response = await apiClient.get("/component-types-options", { params });
    return response.data;
  },

  getComponentTypesByCategory: async (category) => {
    const response = await apiClient.get(`/component-types/category/${category}`);
    return response.data;
  },

  // ==================== TAX CONFIGURATION APIs ====================
  getAllTaxConfigs: async (params) => {
    const response = await apiClient.get("/payroll/tax-configs", { params });
    return response.data;
  },

  createTaxConfig: async (payload) => {
    const response = await apiClient.post("/payroll/tax-configs", payload);
    return response.data;
  },

  getTaxConfig: async (id) => {
    const response = await apiClient.get(`/payroll/tax-configs/${id}`);
    return response.data;
  },

  updateTaxConfig: async (id, payload) => {
    const response = await apiClient.put(`/payroll/tax-configs/${id}`, payload);
    return response.data;
  },

  deleteTaxConfig: async (id) => {
    const response = await apiClient.delete(`/payroll/tax-configs/${id}`);
    return response.data;
  },

  // ==================== PROFESSIONAL TAX RULES APIs ====================
  getProfessionalTaxRules: async () => {
    const response = await apiClient.get("/payroll/professional-tax-rules");
    return response.data;
  },

  addProfessionalTaxRule: async (payload) => {
    const response = await apiClient.post("/payroll/professional-tax-rules", payload);
    return response.data;
  },

  editProfessionalTaxRule: async (id, payload) => {
    const response = await apiClient.put(`/payroll/professional-tax-rules/${id}`, payload);
    return response.data;
  },

  deleteProfessionalTaxRule: async (id) => {
    const response = await apiClient.delete(`/payroll/professional-tax-rules/${id}`);
    return response.data;
  },

  // ==================== SALARY COMPONENTS APIs ====================
  getSalaryComponents: async (params) => {
    const response = await apiClient.get("/payroll/salary-components", { params });
    return response.data;
  },

  addSalaryComponent: async (payload) => {
    const response = await apiClient.post("/payroll/salary-components", payload);
    return response.data;
  },

  updateSalaryComponent: async (id, payload) => {
    const response = await apiClient.put(`/payroll/salary-components/${id}`, payload);
    return response.data;
  },

  deleteSalaryComponent: async (id) => {
    const response = await apiClient.delete(`/payroll/salary-components/${id}`);
    return response.data;
  },

  // ==================== STATUTORY SETTINGS APIs ====================
  getStatutorySettings: async () => {
    const response = await apiClient.get("/payroll/statutory-settings");
    return response.data;
  },

  updateStatutorySettings: async (id, payload) => {
    const response = await apiClient.put(`/payroll/statutory-settings/${id}`, payload);
    return response.data;
  },

  // ==================== LEGACY APIs (kept for backward compatibility) ====================
  // Generate payroll
  generatePayroll: async (payload) => {
    const response = await apiClient.post("/payroll/generate", payload);
    return response.data;
  },

  // Get monthly payrolls
  getMonthlyPayrolls: async (params) => {
    const response = await apiClient.get("/payroll/monthly-payrolls", { params });
    return response.data;
  },

  // Get individual payroll details
  getPayrollDetails: async (id) => {
    const response = await apiClient.get(`/payroll/monthly-payrolls/${id}`);
    return response.data;
  },

  // Update payroll status
  updatePayrollStatus: async (id, payload) => {
    const response = await apiClient.put(
      `/payroll/monthly-payrolls/${id}/status`,
      payload
    );
    return response.data;
  },

  // Get payroll summary
  getPayrollSummary: async (params) => {
    const response = await apiClient.get("/payroll/summary", { params });
    return response.data;
  },

  // Regenerate payroll for specific employee
  regeneratePayroll: async (employeeId, payload) => {
    const response = await apiClient.post(
      `/payroll/regenerate/${employeeId}`,
      payload
    );
    return response.data;
  },

  // Download payslip
  downloadPayslip: async (id) => {
    const response = await apiClient.get(`/payroll/monthly-payrolls/${id}/download`, {
      responseType: "blob",
    });
    return response.data;
  },

  // Bulk download payslips
  bulkDownloadPayslips: async (payload) => {
    const response = await apiClient.post(
      "/payroll/bulk-download-payslips",
      payload,
      {
        responseType: "blob",
      }
    );
    return response.data;
  },

  // Preview payslip
  previewPayslip: async (id) => {
    const response = await apiClient.get(`/payroll/monthly-payrolls/${id}/preview`, {
      responseType: "blob",
    });
    return response.data;
  },
};

export default payrollService;
